import { LinearGradient } from "expo-linear-gradient";
import { memo, useState } from "react";
import { Animated, Platform, Pressable, StyleSheet, View } from "react-native";

import type { Dance } from "@recital-master/shared";
import { getDownloadWindow } from "@recital-master/shared";

import { Artwork } from "@/components/Artwork";
import { DanceDownloadButton } from "@/components/DanceDownloadButton";
import { useAccount } from "@/data/AccountProvider";
import { useMediaUrl } from "@/data/CatalogProvider";
import { Muted, Text } from "@/design/Text";
import { webDenseHoverTransition } from "@/design/interactionMotion";
import {
  focusRing,
  fonts,
  gradients,
  hoverRing,
  palette,
  radius,
  space
} from "@/design/tokens";
import { danceMetaLine, isRecitalIntro } from "@/lib/danceMetadata";
import { displayTitle } from "@/lib/format";
import { useSmoothPlaybackProgress } from "@/playback/useSmoothPlaybackProgress";

type DanceRowProps = {
  dance: Dance;
  onPress: () => void;
  /** Currently playing (watch screen queue). */
  active?: boolean;
  /** The wide watch rail supplies one stationary active surface behind rows. */
  activeSurface?: boolean;
  watched?: boolean;
  /** Where the family left off. */
  resumePoint?: boolean;
  /** Seconds remaining in the resume dance — drives the thumb progress bar. */
  resumeRemainingSeconds?: number;
  /** Live playback fraction (0–1) for the active row — keeps its bar moving. */
  playbackProgress?: number;
  playbackPlaying?: boolean;
  playbackSampleIntervalMs?: number;
  showDancers?: boolean;
  /** Dancer ids to emphasize in the dancer line (saved My Dancers). */
  highlightDancerIds?: Set<string>;
};

const DANCE_ROW_THUMB_WIDTH = 112;
/** Stable queue geometry: 16:9 thumb + row padding + the outer hairline. */
export const DANCE_ROW_HEIGHT =
  DANCE_ROW_THUMB_WIDTH * (9 / 16) + (space.sm + 2) * 2 + 2;
export const DANCE_ROW_ACTIVE_BACKGROUND = "rgba(143, 168, 250, 0.10)";

/** Mouse clicks focus web pressables too; only keyboard focus earns a row ring. */
function isKeyboardFocus(event: { nativeEvent?: unknown }): boolean {
  if (Platform.OS !== "web") return true;
  const target = (event.nativeEvent as { target?: unknown } | undefined)?.target;
  return typeof Element !== "undefined" && target instanceof Element
    ? target.matches(":focus-visible")
    : false;
}

// The outer row owns the keyboard ring. Suppress the global outline on the
// inner play target so it cannot cut through sibling status/download actions.
const noInnerOutline = Platform.select({
  web: { outlineStyle: "none" } as unknown as object,
  default: undefined
});

export const DanceRow = memo(function DanceRow({
  dance,
  onPress,
  active = false,
  activeSurface = true,
  watched = false,
  resumePoint = false,
  resumeRemainingSeconds,
  playbackProgress,
  playbackPlaying = false,
  playbackSampleIntervalMs = 500,
  showDancers = true,
  highlightDancerIds
}: DanceRowProps) {
  const mediaUrl = useMediaUrl();
  const { accessExpiresAt, downloadsUnlockAtByRecital } = useAccount();
  const downloadsUnlocked =
    !isRecitalIntro(dance) &&
    accessExpiresAt != null &&
    getDownloadWindow(
      accessExpiresAt,
      new Date(),
      downloadsUnlockAtByRecital[dance.recitalId]
    ).status === "unlocked";
  // One lit surface for the WHOLE row — play target and trailing utilities
  // glow together (MediaCard's sibling-pressables pattern). Hover tracked on
  // the outer row because the wash must reach past the play pressable.
  const [rowHovered, setRowHovered] = useState(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const rowLit = rowHovered || keyboardFocused;
  const rowHoverProps = Platform.select({
    web: {
      onMouseEnter: () => setRowHovered(true),
      onMouseLeave: () => setRowHovered(false)
    } as object,
    default: {}
  });
  const highlighted = dance.dancers.filter((d) => highlightDancerIds?.has(d.dancerId));
  const dancerLine =
    highlighted.length > 0
      ? highlighted.map((d) => d.name).join(", ")
      : dance.dancers.map((d) => d.name).join(", ");

  // Streaming-app language: progress lives as a thin gradient bar along the
  // thumbnail's bottom edge — live on the playing dance, partial on the
  // resume dance, full on watched dances, absent on untouched ones.
  const resumeProgress = active
    ? playbackProgress != null
      ? Math.min(1, Math.max(0, playbackProgress))
      : null
    : resumePoint && resumeRemainingSeconds != null && dance.durationSeconds > 0
      ? Math.min(1, Math.max(0, 1 - resumeRemainingSeconds / dance.durationSeconds))
      : watched
        ? 1
        : null;
  const progressAnimation = useSmoothPlaybackProgress({
    currentTime: (resumeProgress ?? 0) * dance.durationSeconds,
    duration: dance.durationSeconds,
    freezeWhilePaused: active,
    itemKey: `${dance.danceId}:${active ? "active" : "static"}`,
    mode: "glide",
    playing: active && playbackPlaying,
    sampleIntervalMs: playbackSampleIntervalMs
  });
  const progressWidth = progressAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ["0%", "100%"]
  });

  return (
    <View
      {...rowHoverProps}
      style={[
        styles.row,
        active && activeSurface && styles.rowActive,
        rowLit && !active && styles.rowHovered,
        keyboardFocused &&
          Platform.select({ web: focusRing as object, default: hoverRing as object })
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          isRecitalIntro(dance)
            ? `Play ${displayTitle(dance.title)}`
            : `Play dance ${dance.danceNumber}, ${displayTitle(dance.title)}`
        }
        onPress={onPress}
        onFocus={(event) => setKeyboardFocused(isKeyboardFocus(event))}
        onBlur={() => setKeyboardFocused(false)}
        style={({ pressed }) => [
          styles.playTarget,
          noInnerOutline,
          pressed && styles.rowPressed
        ]}
      >
        <View style={[styles.thumbWrap, watched && !active && styles.dimmed]}>
          <Artwork
            uri={mediaUrl(dance.thumbPath)}
            placeholderUri={dance.blurThumb}
            fallbackSeed={dance.danceId}
            style={styles.thumb}
          />
          <View style={styles.numberBadge}>
            <Text color={palette.text} style={styles.numberText}>
              {isRecitalIntro(dance) ? "Intro" : dance.danceNumber}
            </Text>
          </View>
          {active ? (
            <View style={styles.activeOverlay}>
              <Text color={palette.text} style={{ fontSize: 14 }}>▶</Text>
            </View>
          ) : null}
          {resumeProgress != null ? (
            <View style={styles.progressClip}>
              <View style={styles.progressTrack}>
                {/* Keyed by state so flips (watched → playing → watched) mount a
                    fresh bar at the right width; only live playback glides. */}
                <Animated.View
                  key={active ? "live" : resumePoint ? "resume" : "watched"}
                  style={[styles.progressFillClip, { width: progressWidth }]}
                >
                  <LinearGradient
                    colors={gradients.playback as unknown as [string, string, ...string[]]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.progressFill}
                  />
                </Animated.View>
              </View>
            </View>
          ) : null}
        </View>

        <View style={styles.body}>
          <Text
            variant="danceTitle"
            color={active ? palette.accent : palette.text}
            numberOfLines={1}
          >
            {displayTitle(dance.title)}
          </Text>
          <Muted variant="small" numberOfLines={1}>
            {danceMetaLine(dance)}
          </Muted>
          {showDancers && dancerLine ? (
            <Text
              variant="small"
              color={highlighted.length > 0 ? palette.pink : palette.faint}
              numberOfLines={1}
            >
              {dancerLine}
            </Text>
          ) : null}
        </View>

      </Pressable>
      {/* Trailing utilities live INSIDE the row's lit surface, as one
          cluster on the same gutter the play target opens with. */}
      {watched || downloadsUnlocked ? (
        <View style={styles.trailing}>
          {watched ? (
            <Text color={palette.faint} style={styles.glyph}>✓</Text>
          ) : null}
          {downloadsUnlocked ? <DanceDownloadButton dance={dance} /> : null}
        </View>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    alignItems: "center",
    borderColor: "transparent",
    borderRadius: radius.md,
    borderWidth: 1,
    flexDirection: "row",
    marginHorizontal: -space.md,
    ...webDenseHoverTransition
  },
  playTarget: {
    alignItems: "center",
    borderRadius: radius.md,
    flex: 1,
    flexDirection: "row",
    gap: space.lg,
    paddingHorizontal: space.md,
    paddingVertical: space.sm + 2
  },
  rowHovered: {
    backgroundColor: palette.coolWashSubtle
  },
  rowActive: {
    backgroundColor: DANCE_ROW_ACTIVE_BACKGROUND
  },
  rowPressed: {
    opacity: 0.85
  },
  trailing: {
    alignItems: "center",
    flexDirection: "row",
    flexShrink: 0,
    gap: space.sm,
    paddingRight: space.md
  },
  dimmed: {
    opacity: 0.45
  },
  // The download-button wrapper made the old stretch-to-row-height thumb
  // circular (thumb height ↔ row height) on both engines; a definite width
  // with 16:9 keeps layout deterministic everywhere.
  thumbWrap: {
    aspectRatio: 16 / 9,
    position: "relative",
    width: DANCE_ROW_THUMB_WIDTH
  },
  thumb: {
    borderRadius: radius.sm,
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0
  },
  numberBadge: {
    alignItems: "center",
    backgroundColor: "rgba(8, 9, 14, 0.72)",
    borderRadius: 6,
    bottom: 4,
    justifyContent: "center",
    left: 4,
    minWidth: 22,
    paddingHorizontal: 4,
    paddingVertical: 1,
    position: "absolute"
  },
  numberText: {
    fontFamily: fonts.displaySemi,
    fontSize: 11,
    lineHeight: 15
  },
  activeOverlay: {
    alignItems: "center",
    backgroundColor: "rgba(8, 9, 14, 0.45)",
    borderRadius: radius.sm,
    bottom: 0,
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0
  },
  body: {
    flex: 1,
    gap: 2
  },
  progressClip: {
    borderBottomLeftRadius: radius.sm,
    borderBottomRightRadius: radius.sm,
    bottom: 0,
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0
  },
  progressTrack: {
    backgroundColor: "rgba(243, 244, 250, 0.22)",
    height: 4
  },
  progressFillClip: {
    height: 4,
    overflow: "hidden"
  },
  progressFill: {
    height: 4,
    width: "100%"
  },
  glyph: {
    fontSize: 13,
    opacity: 0.85
  }
});
